import * as React from 'react';
import { LinearProgress } from '@mui/material';
import DocNotFound from '../tool/DocNotFound';
import ErrorMsg from '../common/ErrorMsg';
import Mark from './Mark';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import { AppContext } from '../../App';
import { ITrainingSet, IDocument } from '../../types/my-types';
import { MarkContextProvider } from '../../context/MarkContext';
import { useQuery } from '@tanstack/react-query';
import { ContentWrapper } from '../tool/layout/ToolLayout.styles';

interface MarkDataProps {
  docIndex: number;
  docIds: string[];
  trainingSet: ITrainingSet | undefined;
}

// TODO: refactor into Mark.tsx
const MarkData: React.FC<MarkDataProps> = ({ docIndex, docIds, trainingSet }) => {
  const { dataPoolId, setDocs, setIds, setFeedback } = React.useContext(AppContext);

  React.useEffect(() => {
    setIds(docIds);
    // eslint-disable-next-line
  }, []);

  let offset = Math.floor(docIndex / 100) * 100;
  const glynt = useGlynt(dataPoolId);
  const { isPending, isFetching, data, isError, refetch } = useQuery({
    queryKey: ['documents', trainingSet?.id ?? 'NO_TRAINING_SET'],

    queryFn: async () => {
      if (!trainingSet) {
        return { results: [], count: 0 };
      }
      return (await glynt).document.list({
        training_set: trainingSet.id,
        limit: 100,
        offset,
        advanced: true,
      });
    },

    refetchOnWindowFocus: false,
    enabled: !!trainingSet
  });

  React.useEffect(() => {
    if (!trainingSet) return;

    if (data) {
      setDocs(data.results);
      if (data.results.length <= docIndex) {
        refetch();
      }
    } else {
      refetch();
    }
  }, [data, setDocs, docIndex, refetch, trainingSet]);

  if (!trainingSet || isPending || isFetching) {
    return <LinearProgress />;
  }

  if (isError) {
    setFeedback({
      open: true,
      message: errorResponses.failedToLoadDocuments,
    });
    return <ErrorMsg error={errorResponses.failedToLoadDocuments} />;
  }

  const docs: IDocument[] = data?.results || [];

  if (docIndex < 0) {
    return <DocNotFound />;
  }

  return (
    <MarkContextProvider>
      <div
        className="markViewport"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <ContentWrapper>
          <Mark trainingSet={trainingSet} docIndex={docIndex} docs={docs} />
        </ContentWrapper>
      </div>
    </MarkContextProvider>
  );
};

export default MarkData;
